import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  useGetProjectActivitiesByProjectId,
  useGetProjectDeliverablesByProjectId,
} from "../../../../../../../../api/services/project/projectApis";
import { PAGE_IDS } from "../../../../../../../../helpers/constants";
import { UnitTypesKey } from "../../../../../../../../helpers/constants/siteCoreKeys";
import { isNotEmpty } from "../../../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../../../../redux/slices/appState/appStateSelector";
import {
  keyDetailsSelector,
  projectFrameworkIdSelector,
} from "../../../../../../../../redux/slices/project/projectSelector";
import { CreateClientActivitiesAndDeliverables } from "./ProjectActivitiesAndDeliverables";
import { ProjectCreateActivity } from "./components/createActivity/ProjectCreateActivity";
import { ProjectCreateClientDeliverable } from "./components/createDeliverable/ProjectCreateDeliverable";
import { ProjectActivitiesRowValue } from "./modelsAndSchemas/models";
import { activitiesListSiteCoreKeys } from "./siteCore/SiteCoreKeys";

const pageId = PAGE_IDS.CREATE_CLIENT_ACTIVITY_DELIVERABLES;

interface ProjectActivityAndDeliverableMainProps {
  isAddButtonVisible?: boolean;
}

export const ProjectActivityAndDeliverableMain = ({
  isAddButtonVisible = true,
}: ProjectActivityAndDeliverableMainProps) => {
  const { fetchValueByKeyOfDropdown } = useSiteCore();
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const projectKeyDetails = useSelector(keyDetailsSelector);
  const projectFrameworkId = useSelector(projectFrameworkIdSelector);
  const { id: projectId = "" } = projectKeyDetails || {};
  const [isOpenActivityModal, setIsOpenActivityModal] = useState(false);
  const [isOpenDeliverableModal, setIsOpenDeliverableModal] = useState(false);

  const {
    data: listOfProjectActivitiesData,
    refetchWithParams: getProjectActivity,
    refetch: refetchProjectActivity,
  } = useGetProjectActivitiesByProjectId();

  const {
    data: listOfProjectDeliverablesData,
    refetch: refetchProjectDeliverables,
    refetchWithParams: getProjectDeliverables,
  } = useGetProjectDeliverablesByProjectId();

  useEffect(() => {
    getProjectActivity(projectId);
    getProjectDeliverables(projectId);
  }, [projectFrameworkId]);

  const onCreateDeliverableClick = () => {
    setIsOpenDeliverableModal(true);
  };

  const onCreateActivityClick = () => {
    setIsOpenActivityModal(true);
  };

  const getActivityListDropdownValue = (parentKey: string, key: string) =>
    fetchValueByKeyOfDropdown(
      activitiesListSiteCoreKeys.activitiesList,
      parentKey,
      key,
    );
  const formattedProjectTopics: ProjectActivitiesRowValue[] = useMemo(() => {
    if (isNotEmpty(listOfProjectActivitiesData) && !siteCoreLoading) {
      return listOfProjectActivitiesData.map((item) => {
        const modality = item?.modalityIds?.map((siteCoreKey) =>
          getActivityListDropdownValue(
            activitiesListSiteCoreKeys.modalityList,
            siteCoreKey,
          ),
        );
        const measurement =
          item.units === UnitTypesKey.Minutes
            ? getActivityListDropdownValue(
                activitiesListSiteCoreKeys.minutesList,
                item.measurement,
              )
            : item.measurement;
        const units = getActivityListDropdownValue(
          activitiesListSiteCoreKeys.unitList,
          item.units,
        );
        return {
          ...item,
          id: item?.topic?.id,
          activityType: item?.activityType?.name || "",
          measurement,
          units,
          modality,
          topicName: item?.topic?.name || "",
          activityStatus: item?.activityStatusId,
          projectFramework: item?.projectFramework?.name || "",
        };
      });
    }
    return [];
  }, [listOfProjectActivitiesData, siteCoreLoading]);

  return (
    <>
      <CreateClientActivitiesAndDeliverables
        rows={formattedProjectTopics ?? []}
        deliverableRows={listOfProjectDeliverablesData ?? []}
        isView={!isAddButtonVisible}
        onCreateActivityClick={onCreateActivityClick}
        successHandler={refetchProjectActivity}
        successHandlerDeliverables={refetchProjectDeliverables}
        onCreateDeliverableClick={onCreateDeliverableClick}
      />
      {isOpenActivityModal && (
        <ProjectCreateActivity
          visible={isOpenActivityModal}
          onClickCloseModal={() => setIsOpenActivityModal(false)}
          pageId={pageId}
          getListOfProjectActivitiesApi={refetchProjectActivity}
        />
      )}

      {isOpenDeliverableModal && (
        <ProjectCreateClientDeliverable
          visible={isOpenDeliverableModal}
          handleCloseModal={() => setIsOpenDeliverableModal(false)}
          pageId={pageId}
          getListOfDeliverablesApi={refetchProjectDeliverables}
        />
      )}
    </>
  );
};
